import React, { useEffect, useState } from "react";
import { MessageCircle, Search, ExternalLink, UserPlus, Trash2, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import WhatsAppChat from "@/components/WhatsAppChat";
import { useAuth } from "@/context/AuthContext";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso), s = (Date.now() - d.getTime()) / 1000;
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

export default function WhatsAppInbox() {
  const { user } = useAuth();
  const canDelete = user && (["owner", "manager"].includes(user.role) || user.can_delete);

  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null);
  const [q, setQ] = useState("");
  const [configured, setConfigured] = useState(true);

  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveCompany, setSaveCompany] = useState("");
  const [saving, setSaving] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/whatsapp/conversations");
      const list = data.conversations || [];
      setConvos(list);
      setActive((a) => (a ? list.find((c) => c.phone_norm === a.phone_norm) || a : a));
    } catch { /* ignore */ }
  };

  useEffect(() => {
    api.get("/whatsapp/status").then(({ data }) => setConfigured(data.configured)).catch(() => {});
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  const filtered = convos.filter((c) =>
    !q || (c.name || "").toLowerCase().includes(q.toLowerCase()) || (c.phone_e164 || "").includes(q)
  );

  const openSave = () => {
    setSaveName(active?.name || "");
    setSaveCompany("");
    setSaveOpen(true);
  };

  const saveLead = async () => {
    if (!active) return;
    setSaving(true);
    try {
      const { data } = await api.post("/whatsapp/save-lead", {
        phone_e164: active.phone_e164 || active.phone_norm,
        name: saveName.trim(),
        company: saveCompany.trim(),
      });
      toast.success(data.existing ? `Linked to existing lead "${data.company_name}"` : "Lead created");
      setSaveOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save lead");
    } finally {
      setSaving(false);
    }
  };

  const deleteConvo = async () => {
    if (!active) return;
    setDeleting(true);
    try {
      await api.post("/whatsapp/delete-conversation", { phone_norm: active.phone_norm });
      toast.success("Conversation deleted");
      setDelOpen(false);
      setActive(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't delete conversation");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="whatsapp-inbox">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center"><MessageCircle className="h-5 w-5 text-emerald-600" /></div>
        <div>
          <h1 className="font-display text-2xl font-semibold text-slate-900">WhatsApp</h1>
          <p className="text-slate-500 text-sm">Shared team inbox — one number, everyone replies</p>
        </div>
      </div>

      {!configured && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700">
          WhatsApp isn't connected yet. An admin can set up Twilio credentials to enable it.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Conversation list */}
        <div className="md:col-span-1 bg-white border border-slate-200 rounded-lg overflow-hidden flex flex-col" style={{ maxHeight: "70vh" }}>
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-2.5" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or number" className="pl-8 h-9" data-testid="wa-search" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filtered.length === 0 && <p className="text-center text-xs text-slate-400 py-8">No conversations yet.</p>}
            {filtered.map((c) => (
              <button
                key={c.phone_norm}
                onClick={() => setActive(c)}
                data-testid={`wa-convo-${c.phone_norm}`}
                className={`w-full text-left px-3 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors ${active?.phone_norm === c.phone_norm ? "bg-emerald-50" : ""}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-sm text-slate-800 truncate">{c.name || c.phone_e164 || c.phone_norm}</span>
                  <span className="text-[10px] text-slate-400 shrink-0">{timeAgo(c.last_at)}</span>
                </div>
                <div className="flex items-center justify-between gap-2 mt-0.5">
                  <span className="text-xs text-slate-500 truncate">{c.last_direction === "outbound" ? "You: " : ""}{c.last_body}</span>
                  {c.unread > 0 && <span className="shrink-0 h-4 min-w-4 px-1 rounded-full bg-emerald-500 text-white text-[10px] flex items-center justify-center" data-testid={`wa-unread-${c.phone_norm}`}>{c.unread}</span>}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Active thread */}
        <div className="md:col-span-2">
          {active ? (
            <div>
              <div className="flex items-center justify-between mb-2 gap-2">
                <div className="min-w-0">
                  <p className="font-medium text-slate-800 truncate">{active.name || active.phone_e164}</p>
                  <p className="text-xs text-slate-400">{active.phone_e164}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {active.lead_id ? (
                    <Link to={`/leads/${active.lead_id}`} className="text-xs text-blue-600 hover:underline flex items-center gap-1" data-testid="wa-open-lead">
                      Open lead <ExternalLink className="h-3 w-3" />
                    </Link>
                  ) : (
                    <Button size="sm" variant="outline" onClick={openSave} data-testid="wa-save-lead-btn" className="h-8 gap-1.5">
                      <UserPlus className="h-3.5 w-3.5" /> Save as lead
                    </Button>
                  )}
                  {canDelete && (
                    <Button size="icon" variant="ghost" onClick={() => setDelOpen(true)} data-testid="wa-delete-convo-btn" className="h-8 w-8 text-slate-400 hover:text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
              <WhatsAppChat phoneNorm={active.phone_norm} phoneDisplay={active.phone_e164} heightClass="h-[55vh]" hasLead={!!active.lead_id} contactName={active.name || ""} />
            </div>
          ) : (
            <div className="h-full min-h-[300px] flex items-center justify-center text-slate-400 text-sm border border-dashed border-slate-200 rounded-lg">
              Select a conversation to view messages
            </div>
          )}
        </div>
      </div>

      {/* Save as lead */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent data-testid="wa-save-lead-dialog">
          <DialogHeader>
            <DialogTitle>Save as lead</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Phone: <span className="font-medium text-slate-700">{active?.phone_e164}</span></p>
            <div>
              <label className="text-xs font-medium text-slate-600">Contact name</label>
              <Input value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="e.g. Rahul Sharma" data-testid="wa-save-name" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Company (optional)</label>
              <Input value={saveCompany} onChange={(e) => setSaveCompany(e.target.value)} placeholder="e.g. Sharma Pharma" data-testid="wa-save-company" />
            </div>
            <p className="text-[11px] text-slate-400">If this number already exists as a lead, we'll link the chat to it instead of creating a duplicate.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)}>Cancel</Button>
            <Button onClick={saveLead} disabled={saving} data-testid="wa-save-lead-confirm" className="bg-emerald-600 hover:bg-emerald-700">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save lead"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete conversation */}
      <AlertDialog open={delOpen} onOpenChange={setDelOpen}>
        <AlertDialogContent data-testid="wa-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this conversation?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the whole chat history with {active?.phone_e164} from the inbox. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteConvo} disabled={deleting} data-testid="wa-delete-confirm" className="bg-red-600 hover:bg-red-700">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
